import {
  LayoutModule,
  storage,
  useShowInstructions,
  type ComponentConfig,
  type SchemaInstructionsHelper,
} from '@backstage-components/base';
import {useSubscription} from 'observable-hooks';
import {
  useEffect,
  useMemo,
  useReducer,
  type FC,
  type PropsWithChildren,
  type Reducer,
} from 'react';
import type {JsonObject, JsonValue} from 'type-fest';
import {
  GuestConsentChangedEvent,
  GuestConsentChangedEventName,
} from './guest-consent-changed-event';
import {
  GuestConsentContext,
  type GuestConsentContextValue,
} from './guest-consent-context';
import {
  ComponentDefinition,
  isConsentStatus,
  reactName,
  type ConsentStatus,
} from './GuestConsentProviderDefinition';
import {removeAnalyticsToken} from './use-analytics-token';

export type GuestConsentProviderDefinition = LayoutModule<
  typeof reactName,
  GuestConsentProviderProps
>;

/**
 * Creates a React Context to hold information about guest consent related to
 * cookies and analytics.
 */
export const GuestConsentProvider: FC<
  PropsWithChildren<GuestConsentProviderProps>
> = (props) => {
  const {domainName, scope} = props;
  const {broadcast, observable} = useShowInstructions(
    ComponentDefinition.instructions
  );
  const [state, dispatch] = useReducer(
    contextReducer,
    readInitialData(domainName)
  );
  const {analyticsConsent} = state;
  useSubscription(observable, {
    next: (instruction) => {
      // Pass instructions into the `contextReducer`
      dispatch(instruction);
      // Broadcast the instruction to which flows can react
      switch (instruction.type) {
        case 'GuestCookieConsent:accept-all':
          broadcast({
            type: 'GuestCookieConsent:on-accept-all',
            meta: instruction.meta,
          });
          break;
        case 'GuestCookieConsent:reject-all':
          broadcast({
            type: 'GuestCookieConsent:on-reject-all',
            meta: instruction.meta,
          });
          break;
        case 'GuestCookieConsent:save-preferences':
          broadcast({
            type: 'GuestCookieConsent:on-save-preferences',
            meta: instruction.meta,
          });
          break;
      }
    },
  });
  // Store consent state when it changes
  useEffect(() => {
    // base64 encode the data to make it slightly harder to fiddle with in
    // developer tools
    const stateValue = btoa(JSON.stringify(state));
    storage.setItem(storageKey(domainName), stateValue);
  }, [domainName, state]);
  // Clear the analytics token from cookies when consent changes
  useEffect(() => {
    if (analyticsConsent === 'rejected') {
      removeAnalyticsToken();
    }
    const e: GuestConsentChangedEvent = new CustomEvent(
      GuestConsentChangedEventName,
      {detail: {analyticsConsent, necessaryConsent: 'accepted'}}
    );
    if (scope === 'tests') {
      // Dispatch immediately in tests otherwise tests don't see the update
      // without advancing timers
      document.body.dispatchEvent(e);
    } else {
      // Delay dispatching the event by a frame because otherwise the dispatch for
      // the initial value is not received the listeners with effects in the same
      // render pass
      setTimeout(() => document.body.dispatchEvent(e), 1);
    }
  }, [analyticsConsent, scope]);
  // Listen for changes to consent settings in other tabs
  useEffect(() => {
    const preferencesKey = storageKey(domainName);
    const onStorageChange = (e: StorageEvent): void => {
      if (e.key !== preferencesKey) {
        return;
      }
      // Otherwise read value and broadcast an update, the provider will then
      // receive the update from the local broker and update state
      const nextState = readInitialData(domainName);
      broadcast({
        type: 'GuestCookieConsent:save-preferences',
        meta: {analytics: nextState.analyticsConsent},
      });
    };
    window.addEventListener('storage', onStorageChange);
    return () => window.removeEventListener('storage', onStorageChange);
  }, [broadcast, domainName]);
  // Only change the context value when a constituent value changes
  const value: GuestConsentContextValue = useMemo(
    () => ({analyticsConsent, necessaryConsent: 'accepted'}),
    [analyticsConsent]
  );
  return (
    <GuestConsentContext.Provider value={value} children={props.children} />
  );
};

export interface GuestConsentProviderProps {
  /** Domain for which the guest consent is stored */
  domainName?: string;
  /**
   * Where the module is being rendered. Scope changes during component lifetime
   * lead to undefined/unknown behavior.
   */
  scope?: ComponentConfig['scope'];
}

type GuestConsentProviderAction = SchemaInstructionsHelper<
  typeof ComponentDefinition
>;

type GuestConsentProviderState = Omit<
  GuestConsentContextValue,
  'necessaryConsent'
>;

/**
 * Collect consent values and update them when analytics related instructions
 * are received.
 */
const contextReducer: Reducer<
  GuestConsentProviderState,
  GuestConsentProviderAction
> = (draft, action) => {
  switch (action.type) {
    case 'GuestCookieConsent:accept-all':
      return {analyticsConsent: 'accepted'};
    case 'GuestCookieConsent:reject-all':
      return {analyticsConsent: 'rejected'};
    case 'GuestCookieConsent:save-preferences':
      return {
        analyticsConsent: action.meta.analytics ?? draft.analyticsConsent,
      };
    default:
      return draft;
  }
};

/**
 * Get a valid `ConsentStatus` from any string possible string
 * @private exported for tests
 */
export const consentStatusProvider = (s?: string): ConsentStatus =>
  isConsentStatus(s) ? s : 'default';

/**
 * Get the value of `key` from `o` as a `ConsentStatus`
 * @private exported for tests
 */
export function extractConsentStatus(
  o: JsonObject,
  key: string
): ConsentStatus {
  const value = o[key];
  return typeof value === 'string'
    ? consentStatusProvider(value)
    : consentStatusProvider();
}

function readInitialData(
  domainName: string | undefined
): GuestConsentProviderState {
  const fallbackState: GuestConsentProviderState = {
    analyticsConsent: 'default',
  };
  try {
    const initial: JsonValue = JSON.parse(
      // e30= is "{}" base64 encoded
      atob(storage.getItem(storageKey(domainName)) ?? 'e30=')
    );
    if (
      typeof initial === 'object' &&
      initial !== null &&
      !Array.isArray(initial)
    ) {
      return {
        analyticsConsent: extractConsentStatus(initial, 'analyticsConsent'),
      };
    } else {
      return fallbackState;
    }
  } catch {
    // If there is an exception while getting initial data use the fallback
    return fallbackState;
  }
}

/**
 * Compute the key used in local storage for saving consent details based on
 * the provided `domainName`
 * @private exported for tests
 */
export function storageKey(domainName?: string): string {
  return ['lcd', `${domainName}`, 'data-consent-v1'].join('/');
}
